import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon, { COLORS, ICONS } from '../icon';
import Checkbox from '../checkbox/checkbox';
import Switch from '../switch/switch';
import ReactDOM from 'react-dom';

export interface Language {
	label: string;
	code: string;
}

interface Props {
	onChange: (tags: string[], defaultLang: string) => void;
	languages: Language[];
	label?: string;
	placeholder?: string;
	tooltip?: [string | (() => JSX.Element), string];
	renderTooltip?: () => JSX.Element;
	initialValues?: string[];
	initialDefaultLanguage?: string;
}

interface TagProps {
	lang: Language;
	remove: (lang: Language) => void;
}

function Tag(props: TagProps) {
	return useMemo(() => (
		<div className="tag">
			<span>
				{props.lang.label}{' '}
				<button
					className="no-style"
					onClick={() => props.remove(props.lang)}
				>
					<Icon
						name={ICONS.CLOSE}
						size={10}
						color={COLORS.DEFAULT_GRAY}
					/>
				</button>
			</span>
		</div>
	), [props]);
}
//https://brandlive-upload.s3-us-west-2.amazonaws.com/uploads/17/documents/e1s08b9f73/Screen_Shot_2020-11-02_at_9.04.59_AM.png
export default function LanguageSelectInput(props: Props): JSX.Element {
	const {
		onChange,
		languages,
		label,
		placeholder,
		tooltip,
		initialValues,
		initialDefaultLanguage = '',
	} = props;

	const [langs, setLangs] = useState<Language[]>([]);
	const [open, setOpen] = useState(false);
	const [entered, setEntered] = useState(false);
	const tagSelector = useRef<any>(null);
	const [defaultLang, setDefaultLang] = useState(initialDefaultLanguage);
	const [visibleLangs, setVisibleLangs] = useState(languages);
	const [searchTerm, setSearchTerm] = useState('');
	const [image, text] = tooltip || [];
	const [left] = useState(0);
	const [top, setTop] = useState(0);
	const [width, setWidth] = useState(100);
	const [initialized, setInitialized] = useState(false);
	const inputRef = useRef<HTMLInputElement | null>(null);

	const removeLang = useCallback((lang: Language | string) => {
		setLangs((langs: Language[]) =>
			langs.filter(
				(_lang: Language) =>
					_lang.code !== (typeof lang === 'string' ? lang : lang.code)
			)
		);
	}, []);

	const addLang = useCallback((lang: Language | string) => {
		if (typeof lang === 'string') {
			const langSet = new Set(langs);
			const _lang = languages.find(
				(language: Language) => language.code === lang
			);
			if (_lang) { langSet.add(_lang); }
			setLangs(Array.from(langSet.values()));

		} else {
			//make set to ensure that the tags are always unique - will prevent any duplicates
			const langSet = new Set(langs);
			langSet.add(lang);
			setLangs(Array.from(langSet.values()));
		}

		if (inputRef.current) {
			inputRef.current.value = "";
			inputRef.current.focus();
			setSearchTerm('');
		}
	}, [langs, languages]);

	const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		const target = e.target as HTMLInputElement;
		setSearchTerm(target?.value ?? '');
	}, []);
	const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			const match = visibleLangs.length === 1 ? visibleLangs[0] : false;
			if (match) {
				addLang(match);
			}
		}
	}, [visibleLangs, addLang]);

	function handleFocus() {
		setOpen(true);
	}

	const handleClick = useCallback((e: any) => {
		const path = e.path || e.composedPath();

		for (const item of path) {
			if (tagSelector.current === item) {
				inputRef.current?.focus();
				return;
			}
		}

		setOpen(false);
	}, []);

	const handleCheck = useCallback((value: string | number, isOn: boolean) => {
		if (isOn) {
			addLang(value as string);
		} else {
			removeLang(value as string);
		}
	}, [addLang, removeLang]);

	const handleSwitch = useCallback((value: string, isOn: boolean) => {
		setDefaultLang(isOn ? value : '');
	}, []);

	const langsArr = useMemo(() => {
		return langs.map((lang: Language) => lang.code);
	}, [langs]);

	// initialize and values
	useEffect(() => {
		if (initialValues?.length && !initialized) {
			setInitialized(true);
			const langSet = new Set(langs);
			initialValues.forEach((initLang: string) => {
				const _lang = languages.find(
					(language: Language) => language.code === initLang
				);
				if (_lang) { langSet.add(_lang); }
			});
			setLangs(Array.from(langSet.values()));
		}
	}, [initialValues, initialized, langs, languages]);

	useEffect(() => {
		if (searchTerm.trim() === '') {
			setVisibleLangs(languages);
		} else {
			setVisibleLangs(
				languages.filter((lang: Language) =>
					lang.label
						.toLowerCase()
						.includes(searchTerm.toLowerCase().trim())
				)
			);
		}
	}, [searchTerm, languages]);

	useEffect(() => {
		if (open) { window.addEventListener('click', handleClick); }

		return () => window.removeEventListener('click', handleClick);
	}, [handleClick, open]);

	useEffect(() => {
		setEntered(langs.length > 0);
		onChange(
			langs.map((lang: Language) => lang.code),
			defaultLang
		);
	}, [langs, defaultLang, onChange]);

	useEffect(() => {
		ReactDOM.unstable_batchedUpdates(() => {
			setTop((tagSelector.current?.offsetTop ?? 0) + tagSelector.current?.offsetHeight + 28);
			setWidth(tagSelector.current?.offsetWidth ?? 0);
		});
	}, [open, langs]);

	// on tab, close dropdown
	useEffect(() => {
		const handleTab = (event: KeyboardEvent) => {
			if (event.code === 'Tab' || event.key === 'Tab') {
				setOpen(false);
			}
		};
		document.addEventListener('keydown', handleTab);
		return () => {
			document.removeEventListener('keydown', handleTab);
		};
	}, []);

	return (
		<div
			className={classNames('field-group tag-select-container', {
				entered,
			})}
			ref={tagSelector}
		>
			<div style={{ display: 'flex', alignItems: 'center' }}>
				{label && <label>{label}</label>}
				<div className="tooltip">
					{tooltip ? (
						typeof image === 'function' ? (
							image()
						) : (
							<img
								src={image}
								alt="tooltip"
								height="18px"
								width="auto"
							/>
						)
					) : null}{' '}
					<span className="tooltip-text">{tooltip && text}</span>
				</div>
			</div>
			<div className="tag-select">
				{langs.map((lang: Language) => (
					<Tag key={lang.code} lang={lang} remove={removeLang} />
				))}
				<input
					type="text"
					onFocus={handleFocus}
					placeholder={placeholder}
					onKeyDown={handleKeyDown}
					onKeyUp={handleKeyUp}
					ref={inputRef}
				/>
			</div>
			<div
				className="select-dropdown-container"
				style={{ pointerEvents: open ? 'auto' : 'none' }}
			>
				<div
					className={classNames('tag-select-dropdown', { open })}
					style={{ left, top, width }}
				>
					{visibleLangs.map((lang: Language) => (
						<div
							key={lang.code}
							className="tag-option"
							onClick={() => addLang(lang)}
						>
							<div className="tag-left">
								<Checkbox
									onChange={handleCheck}
									checked={langsArr.includes(lang.code)}
									value={lang.code}
								/>
								<span>
									{lang.label}
									{lang.code === defaultLang
										? ' (default)'
										: ''}
								</span>
							</div>
							<div
								className={classNames('tag-right', {
									'is-default': lang.code === defaultLang,
								})}
							>
								<span className="default-label">
									{lang.code === defaultLang
										? 'Default'
										: 'Make default'}
								</span>
								<Switch
									value={lang.code}
									on={lang.code === defaultLang}
									onClick={handleSwitch}
								/>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}